import React from 'react';
import { cn } from '../../shared/util/cn';

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string;
  alt?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  fallback?: string;
}

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, src, alt, size = 'md', fallback, children, ...props }, ref) => {
    const sizes = {
      sm: 'h-8 w-8 text-xs',
      md: 'h-10 w-10 text-sm',
      lg: 'h-12 w-12 text-base',
      xl: 'h-16 w-16 text-lg',
    };

    const [imgError, setImgError] = React.useState(false);

    return (
      <div
        ref={ref}
        className={cn(
          'relative flex shrink-0 overflow-hidden rounded-full border border-[var(--color-mockup-border)]/80 bg-[var(--color-surface-secondary)] shadow-[inset_0_1px_0_rgba(255,255,255,0.22)]',
          sizes[size],
          className
        )}
        {...props}
      >
        {src && !imgError ? (
          <img
            src={src}
            alt={alt}
            className="h-full w-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : children ? (
          <div className="flex h-full w-full items-center justify-center">
            {children}
          </div>
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-primary/12 text-primary font-bold">
            {fallback ? (
              <span className="font-heading uppercase tracking-wide">
                {fallback.slice(0, 2).toUpperCase()}
              </span>
            ) : (
              <svg
                className="h-[52%] w-[52%] text-[var(--color-mockup-text-light)]"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
        )}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';
